import { useEffect, useState } from 'react';

/**
 * useDominantColor(src): computes a dominant color for an image URL using a small canvas sample.
 * - Returns { color, ready }
 * - Falls back gracefully if cross-origin taints canvas or errors occur.
 */
export default function useDominantColor(src) {
  const [color, setColor] = useState('#f1f5f9'); // subtle surface
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!src) {
      setReady(true);
      return;
    }
    let cancelled = false;
    const img = new Image();
    // Try to enable CORS-friendly load for Firebase/CloudFront/CDN if configured
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = src;
    img.onload = () => {
      if (cancelled) return;
      try {
        const w = 32, h = 32;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        let r = 0, g = 0, b = 0, count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha < 16) continue; // skip near-transparent
          r += data[i + 0];
          g += data[i + 1];
          b += data[i + 2];
          count++;
        }
        if (count > 0) {
          r = Math.round(r / count);
          g = Math.round(g / count);
          b = Math.round(b / count);
          const hex = `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
          setColor(hex);
        }
      } catch (e) {
        // canvas tainted or error; keep fallback color
      } finally {
        setReady(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) setReady(true);
    };
    return () => { cancelled = true; };
  }, [src]);

  return { color, ready };
}
